/**
 * useNotifications Hook
 *
 * React hook for sending compliance email notifications from anywhere in the app.
 * Provides fire-and-forget notification sending with comprehensive error handling.
 *
 * USAGE:
 * ```javascript
 * const { sendNotification, sending } = useNotifications();
 *
 * await sendNotification('CERT_EXPIRY_VESSEL', {
 *   certificateName: 'Ship\'s Certificate',
 *   vesselName: 'MV Example',
 *   expiryDate: '2026-03-01',
 *   daysUntil: 7
 * }, ['master@example.com', 'dpa@example.com']);
 * ```
 *
 * NOTIFICATION TYPES:
 * - CERT_EXPIRY_VESSEL: Vessel certificate expiring
 * - CERT_EXPIRY_CREW: Crew certificate expiring
 * - HOR_VIOLATION: Hours of Rest violation (immediate)
 * - DRILL_OVERDUE: Safety drill overdue
 * - MAINT_OVERDUE: Maintenance task overdue
 *
 * ERROR HANDLING:
 * - Non-blocking: Failures logged, not thrown
 * - Returns success/failure status
 * - Safe to use in fire-and-forget pattern
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../components/tailwind/auth/AuthGate';

export const useNotifications = () => {
  const { session } = useAuth();
  const [sending, setSending] = useState(false);

  /**
   * Send a compliance notification email
   *
   * @param {string} type - Notification type (CERT_EXPIRY_VESSEL, etc.)
   * @param {object} data - Notification data (certificate details, crew info, etc.)
   * @param {string[]} recipients - Array of email addresses
   * @returns {Promise<{success: boolean, sent?: number, failed?: number, error?: string}>}
   */
  const sendNotification = useCallback(async (type, data, recipients) => {
    // Validate session
    if (!session?.token) {
      console.warn('[useNotifications] No session, skipping notification');
      return { success: false, error: 'No session' };
    }

    // Validate recipients
    if (!recipients || !Array.isArray(recipients) || recipients.length === 0) {
      console.warn('[useNotifications] No recipients provided');
      return { success: false, error: 'No recipients' };
    }

    // Validate type
    const validTypes = ['CERT_EXPIRY_VESSEL', 'CERT_EXPIRY_CREW', 'HOR_VIOLATION', 'DRILL_OVERDUE', 'MAINT_OVERDUE'];
    if (!validTypes.includes(type)) {
      console.error('[useNotifications] Invalid notification type:', type);
      return { success: false, error: `Invalid type: ${type}` };
    }

    setSending(true);

    try {
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-notification`;

      console.log(`[useNotifications] Sending ${type} to ${recipients.length} recipient(s)`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type, data, recipients })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP ${response.status}`);
      }

      console.log(`[useNotifications] Success: ${type} sent to ${result.sent}/${recipients.length} recipients`);

      return {
        success: true,
        sent: result.sent,
        failed: result.failed,
        type: result.type
      };

    } catch (error) {
      console.error('[useNotifications] Failed to send notification:', error);
      return {
        success: false,
        error: error.message || 'Unknown error'
      };
    } finally {
      setSending(false);
    }
  }, [session]);

  return {
    sendNotification,
    sending
  };
};
